import { useState, useEffect } from 'react';

type CookiePrefs = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const STORAGE_KEY = 'elle_cookie_consent';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (accepted: CookiePrefs) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...accepted, date: new Date().toISOString() }));
    setVisible(false);
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => saveConsent({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => saveConsent(prefs);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div
        className="max-w-5xl mx-auto rounded-2xl border border-gray-700/60 bg-[#0f0f18]/95 backdrop-blur-md overflow-hidden"
        style={{ boxShadow: '0 -4px 40px rgba(220,38,38,0.08), 0 0 0 1px rgba(255,255,255,0.04)' }}
      >
        {/* Top accent line */}
        <div className="h-0.5 w-full bg-gradient-to-r from-red-600 via-orange-500 to-transparent" />

        <div className="p-5 md:p-6">
          {/* Main row */}
          <div className="flex flex-col lg:flex-row lg:items-start gap-5">
            {/* Icon + Text */}
            <div className="flex items-start gap-4 flex-1">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-orange-950/50 border border-orange-700/40 mt-0.5">
                <i className="ri-shield-check-line text-orange-400 text-lg" />
              </div>
              <div>
                <h4 className="font-['Exo_2'] font-bold text-white text-sm tracking-wide mb-1">
                  Utilizziamo i Cookie
                </h4>
                <p className="font-['Inter'] text-gray-400 text-xs leading-relaxed max-w-2xl">
                  Questo sito utilizza cookie tecnici necessari al funzionamento e, previo consenso, cookie analitici e di marketing.
                  Puoi accettare tutti i cookie, rifiutare quelli non essenziali o personalizzare le tue preferenze.
                  Per maggiori informazioni consulta la nostra{' '}
                  <button
                    type="button"
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-orange-400 hover:text-orange-300 underline underline-offset-2 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cookie Policy
                  </button>
                  {' '}ai sensi del Reg. (UE) 2016/679 (GDPR).
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-2 lg:flex-shrink-0">
              <button
                onClick={acceptAll}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white font-['Exo_2'] font-bold text-xs tracking-widest uppercase rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Accetta Tutti
              </button>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-5 py-2.5 border border-gray-600 hover:border-orange-500/60 text-gray-300 hover:text-white font-['Exo_2'] font-bold text-xs tracking-widest uppercase rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Personalizza
              </button>
              <button
                onClick={rejectAll}
                className="px-5 py-2.5 border border-gray-800 hover:border-gray-600 text-gray-500 hover:text-gray-300 font-['Exo_2'] font-bold text-xs tracking-widest uppercase rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Rifiuta
              </button>
            </div>
          </div>

          {/* Expanded details */}
          {showDetails && (
            <div className="mt-5 pt-5 border-t border-gray-800/60">
              <p className="font-['Exo_2'] text-xs text-gray-500 tracking-widest uppercase mb-4">
                Gestisci le preferenze cookie
              </p>
              <div className="flex flex-col gap-3">
                {/* Necessary */}
                <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-[#08080c] border border-gray-800/50">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-['Exo_2'] font-bold text-white text-xs tracking-wide">Cookie Tecnici Necessari</span>
                      <span className="px-2 py-0.5 rounded-full bg-green-950/50 border border-green-800/50 text-green-400 font-['Inter'] text-xs">Sempre attivi</span>
                    </div>
                    <p className="font-['Inter'] text-gray-500 text-xs leading-relaxed">
                      Essenziali per il corretto funzionamento del sito. Non possono essere disabilitati. Includono cookie di sessione, preferenze lingua e sicurezza.
                    </p>
                  </div>
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-10 h-5 rounded-full bg-green-700/60 flex items-center justify-end px-0.5">
                      <div className="w-4 h-4 rounded-full bg-green-400" />
                    </div>
                  </div>
                </div>

                {/* Analytics */}
                <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-[#08080c] border border-gray-800/50">
                  <div className="flex-1">
                    <span className="font-['Exo_2'] font-bold text-white text-xs tracking-wide block mb-1">Cookie Analitici</span>
                    <p className="font-['Inter'] text-gray-500 text-xs leading-relaxed">
                      Ci aiutano a capire come i visitatori interagiscono con il sito, raccogliendo informazioni in forma anonima (es. Google Analytics).
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}
                    className={`flex-shrink-0 mt-0.5 w-10 h-5 rounded-full transition-all duration-300 flex items-center px-0.5 cursor-pointer ${
                      prefs.analytics ? 'bg-red-600 justify-end' : 'bg-gray-700 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 rounded-full bg-white" />
                  </button>
                </div>

                {/* Marketing */}
                <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-[#08080c] border border-gray-800/50">
                  <div className="flex-1">
                    <span className="font-['Exo_2'] font-bold text-white text-xs tracking-wide block mb-1">Cookie di Marketing</span>
                    <p className="font-['Inter'] text-gray-500 text-xs leading-relaxed">
                      Utilizzati per mostrare annunci pertinenti e misurare l&apos;efficacia delle campagne pubblicitarie. Possono essere condivisi con partner terzi.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPrefs(p => ({ ...p, marketing: !p.marketing }))}
                    className={`flex-shrink-0 mt-0.5 w-10 h-5 rounded-full transition-all duration-300 flex items-center px-0.5 cursor-pointer ${
                      prefs.marketing ? 'bg-red-600 justify-end' : 'bg-gray-700 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 rounded-full bg-white" />
                  </button>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={saveCustom}
                  className="px-6 py-2.5 bg-orange-600 hover:bg-orange-500 text-white font-['Exo_2'] font-bold text-xs tracking-widest uppercase rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Salva Preferenze
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
